import PropTypes from 'prop-types';
import React, {cloneElement, useCallback, useMemo} from 'react';
import {Link, useRouteMatch} from 'react-router-dom';
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  lighten,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {lightGrey} from '@account/styles';
import {trackEvent} from '@account/analytics';

const Text = styled(ListItemText)(({theme}) => ({
  opacity: 1,
  transition: 'opacity .15s ease',
  [theme.breakpoints.only('sm')]: {
    opacity: 0,
  },
}));

const SidebarListItem = ({
  icon,
  text,
  to,
  exact,
  search,
  disabled,
  nested,
  parent,
  id,
  component = Link,
  ...props
}) => {
  const selected = !!useRouteMatch({path: to, exact});
  const parentOf = !!useRouteMatch({path: to, exact: false});
  const theme = useTheme();
  const large = useMediaQuery(theme.breakpoints.up('md'));
  const small = useMediaQuery(theme.breakpoints.only('sm'));

  const close = useCallback(() => {
    trackEvent('Clicked on Sidebar Navigation', {
      name: id,
      url: to,
    });
  }, [id, to]);

  const qeTag = useMemo(
    () =>
      to
        ?.split('/')
        .filter(item => !Number(item))
        .filter(String)
        .join('-'),
    [to]
  );

  return (
    <Tooltip
      title={text}
      placement="right"
      disableFocusListener={large}
      disableHoverListener={large}
      disableTouchListener={large}
    >
      <ListItemButton
        disabled={disabled}
        to={{
          pathname: to,
          search,
        }}
        onClick={close}
        selected={selected}
        component={component}
        sx={{
          '&:hover': {
            backgroundColor:
              nested || parentOf
                ? lighten(theme.palette.primary.light, 0.6)
                : theme.palette.primary.lighter,
          },
          backgroundColor:
            parentOf || nested
              ? lighten(theme.palette.primary.lighter, 0.4)
              : 'white',
          boxShadow:
            parentOf || nested
              ? `inset -2px 0 0 ${theme.palette.primary.main}`
              : 'none',

          borderTop:
            parent && parentOf && !selected ? `solid 1px ${lightGrey}` : 'none',
        }}
        data-qe={`${qeTag}-sidebarlistitem`}
        {...props}
      >
        <ListItemIcon>
          {(small || !nested) &&
            cloneElement(icon, {
              sx: {
                height: nested && large ? 20 : '1em',
                width: nested && large ? 20 : '1em',
                ...icon.props.sx,
              },
            })}
        </ListItemIcon>

        <Text
          primary={text}
          primaryTypographyProps={{
            noWrap: true,
            style: {fontWeight: selected ? 700 : 400},
          }}
        />
      </ListItemButton>
    </Tooltip>
  );
};

SidebarListItem.propTypes = {
  disabled: PropTypes.bool,
  icon: PropTypes.node,
  text: PropTypes.string.isRequired,
  to: PropTypes.string,
  exact: PropTypes.bool,
  nested: PropTypes.bool,
  parent: PropTypes.bool,
  id: PropTypes.string,
  search: PropTypes.string,
  component: PropTypes.any,
};

export default SidebarListItem;
